import instance from '../api';

/**
 * Multipart contact create/update: use the shared axios instance so the request interceptor
 * clears default `Content-Type: application/json` and the runtime sets
 * `multipart/form-data` with a proper boundary (required or the server returns 415).
 * Do not pass `Content-Type: multipart/form-data` manually without a boundary.
 */
async function contactMultipartRequest(method, path, formData) {
  const pathNorm = path.startsWith('/') ? path : `/${path}`;
  const response = await instance.request({
    method,
    url: pathNorm,
    data: formData,
  });
  return { data: response.data };
}

// Google Auth API — OAuth URL + status (safe after login; does not expose secrets)
export const googleAuthAPI = {
  getAuthUrl: () => instance.get('/google-auth/auth-url'),
  getStatus: () => instance.get('/google-auth/status'),
};

// Booking Templates API
// GET /api/customer/booking-templates — Get all templates
// POST /api/customer/booking-templates — Create template
// GET /api/customer/booking-templates/template/:contactId — Get template by contactId
// PUT /api/customer/booking-templates/template/:contactId — Update template
// DELETE /api/customer/booking-templates/template/:contactId — Delete template
// PATCH /api/customer/booking-templates/template/:contactId/toggle-status — Toggle status
export const bookingTemplatesAPI = {
  // Get all booking templates — response: { success, data: [] }
  getTemplates: () => {
    return instance.get('/customer/booking-templates');
  },

  // Get template by contactId — GET /api/customer/booking-templates/template/:contactId
  getTemplateByContactId: (contactId) => {
    return instance.get(`/customer/booking-templates/template/${contactId}`);
  },

  // Create new template — POST with request body
  createTemplate: (templateData) => {
    return instance.post('/customer/booking-templates', templateData);
  },

  // Update existing template — PUT with contactId
  updateTemplate: (contactId, templateData) => {
    return instance.put(`/customer/booking-templates/template/${contactId}`, templateData);
  },

  // Delete template by contactId
  deleteTemplate: (contactId) => {
    return instance.delete(`/customer/booking-templates/template/${contactId}`);
  },

  // Toggle template status
  // Swagger: PUT /api/customer/booking-templates/template/toggle-status
  // Body: { contactId, isActive }
  toggleTemplateStatus: async (contactId, isActive) => {
    const payload = {
      contactId,
      isActive: Boolean(isActive),
    };

    try {
      // Swagger-aligned route
      return await instance.put('/customer/booking-templates/template/toggle-status', payload);
    } catch (err) {
      // Fallback for older/alternative backend route shapes
      const status = err?.response?.status;
      if (status === 404 || status === 405) {
        return await instance.patch(`/customer/booking-templates/template/${contactId}/toggle-status`, payload);
      }
      throw err;
    }
  }
};

// Users API
export const usersAPI = {
  // Get all users with optional filters
  getUsers: (filters = {}, page = 0, limit = 50) => {
    let url = '/users';
    const params = new URLSearchParams();

    // Add filters to params
    if (filters.type) params.append('type', filters.type);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
    if (filters.search) params.append('search', filters.search);

    params.append('page', page);
    params.append('limit', limit);

    return instance.get(`${url}?${params.toString()}`);
  },

  // Get user by ID
  getUser: (userId) => {
    return instance.get(`/users/${userId}`);
  }
};

// Contacts API
export const contactsAPI = {
  // GET /api/customer/contacts — Swagger: query page (1-based, default 1), limit (default 10)
  getContacts: (cityId = '', categoryId = '', pageZeroBased = 0, rowsPerPage = 10) => {
    const url = '/customer/contacts';
    const params = new URLSearchParams();
    if (cityId) params.append('cityId', cityId);
    if (categoryId) params.append('categoryId', categoryId);
    const pageOneBased = Math.max(1, (Number(pageZeroBased) || 0) + 1);
    const limit = Math.max(1, Number(rowsPerPage) || 10);
    params.append('page', String(pageOneBased));
    params.append('limit', String(limit));
    const qs = params.toString();
    return instance.get(qs ? `${url}?${qs}` : url);
  },

  // Get contact by ID
  getContact: (contactId) => {
    return instance.get(`/customer/contacts/${contactId}`);
  },

  // Create new contact (JSON body)
  createContact: (contactData) => {
    return instance.post('/customer/contacts', contactData);
  },

  /** Create contact with multipart (JSON in `payload` + files: BannerImages[], LogoPicture) */
  createContactMultipart: (formData) => {
    return contactMultipartRequest('POST', '/customer/contacts', formData);
  },

  // Update existing contact (JSON body)
  updateContact: (contactId, formData) => {
    return instance.put(`/customer/contacts/${contactId}`, formData);
  },

  /** Update contact with multipart (JSON in `payload` + optional files) */
  updateContactMultipart: (contactId, formData) => {
    return contactMultipartRequest('PUT', `/customer/contacts/${contactId}`, formData);
  },

  // Delete contact
  deleteContact: (contactId) => {
    return instance.delete(`/customer/contacts/${contactId}`);
  },

  // Update contact approval status
  updateContactApproval: (data) => {
    return instance.patch(`/contacts/${data.id}/approval`, {
      approvalStatus: data.approvalStatus,
      approvalNote: data.approvalNote
    });
  },

  /**
   * PUT /customer/contacts/status
   * Backend consumes multipart / form-urlencoded (not JSON). Use FormData so the crypto
   * interceptor can encrypt fields into `data` — URLSearchParams stringifies to "{}".
   */
  updateContactStatus: (contactId, isActive) => {
    const id = Number.parseInt(String(contactId), 10);
    if (!Number.isFinite(id)) {
      return Promise.reject(new Error('Invalid ContactId'));
    }
    const body = new FormData();
    body.append('ContactId', String(id));
    body.append('IsActive', Boolean(isActive) ? 'true' : 'false');
    return instance.put('/customer/contacts/status', body);
  }
};

// Cities API
export const citiesAPI = {
  // Get all cities
  getCities: () => {
    return instance.get('/customer/cities?Page=1&PageSize=1000');
  },

  // Get city by ID
  getCity: (cityId) => {
    return instance.get(`/cities/${cityId}`);
  }
};

// Categories API
export const categoriesAPI = {
  // Get categories by city
  getCategories: (cityId) => {
    // Some backends require cityId (filtering), while others return all categories without it.
    // Support both to make the UI dropdown work in more flows.
    if (cityId) return instance.get(`/customer/categories?cityId=${cityId}`);
    return instance.get('/customer/categories');
  },

  // Get category by ID
  getCategory: (categoryId) => {
    return instance.get(`/categories/${categoryId}`);
  }
};

// Districts API
// SubCategories API
export const subCategoriesAPI = {
  /** GET /api/customer/categories/subcategories?serviceCategoryId={id} */
  getSubCategories: (serviceCategoryId) => {
    return instance.get('/customer/categories/subcategories', {
      params: { serviceCategoryId }
    });
  }
};

export const districtsAPI = {
  // Get districts for a city
  getDistricts: (cityId, language = 'en') => {
    return instance.get('/customer/districts', { params: { cityId, language } });
  },
  getDistrictss: (cityId) => {
    return instance.get(`/customer/districts?cityId=${cityId}`);
  },
  // Get district by ID
  getDistrict: (districtId) => {
    return instance.get(`/districts/${districtId}`);
  }
};

// Bookings API
export const bookingsAPI = {
  // Get bookings list with filters & pagination
  getBookings: (filters = {}, page = 0, limit = 10) => {
    const url = '/customer/bookings';
    const params = new URLSearchParams();

    params.append('page', page + 1); // backend is 1-indexed
    params.append('limit', limit);

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    return instance.get(`${url}?${params.toString()}`);
  },

  getBooking: (bookingId) => instance.get(`/customer/bookings/${bookingId}`),

  /** GET /api/customer/bookings/{bookingId}/popup */
  getBookingPopup: (bookingId) => instance.get(`/customer/bookings/${bookingId}/popup`),

  createBooking: (payload) => instance.post('/customer/bookings/guest', payload),

  updateBooking: (bookingId, payload) => instance.put(`/customer/bookings/${bookingId}`, payload),

  deleteBooking: (bookingId, reason) => instance.delete(`/customer/bookings/${bookingId}`, { data: reason }),

  /** PUT /api/customer/bookings/status — body: { bookingId, status, notes, notifyCustomer } */
  updateStatus: (payload) => instance.put('/customer/bookings/status', payload),

  transferBooking: (bookingId, payload) => instance.post(`/customer/bookings/${bookingId}/transfer`, payload),

  rescheduleBooking: (bookingId, payload) => instance.post(`/customer/bookings/${bookingId}/reschedule`, payload),

  bulkActions: (payload) => instance.post('/customer/bookings/bulk-actions', payload),

  getTodayBookings: (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return instance.get(`/customer/bookings/today?${params}`);
  },

  getUpcomingBookings: (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return instance.get(`/customer/bookings/upcoming?${params}`);
  },

  getAnalytics: (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return instance.get(`/customer/bookings/analytics?${params}`);
  },

  sendReminder: (bookingId, payload) => instance.post(`/customer/bookings/${bookingId}/reminders`, payload)
};

// User booking block management API
export const userBookingBlocksAPI = {
  /** GET /api/customer/user-booking-blocks/users?page&pageSize */
  getUsers: (page = 1, pageSize = 10) =>
    instance.get('/customer/user-booking-blocks/users', {
      params: {
        page: Math.max(1, Number(page) || 1),
        pageSize: Math.max(1, Number(pageSize) || 10),
      },
    }),

  /** GET /api/customer/user-booking-blocks/users/{appUserId}/details?page&pageSize */
  getUserDetails: (appUserId, page = 1, pageSize = 20) =>
    instance.get(`/customer/user-booking-blocks/users/${appUserId}/details`, {
      params: {
        page: Math.max(1, Number(page) || 1),
        pageSize: Math.max(1, Number(pageSize) || 20),
      },
    }),

  /** POST /api/customer/user-booking-blocks/{appUserId} — body: { reason } */
  blockUser: (appUserId, reason) =>
    instance.post(`/customer/user-booking-blocks/${appUserId}`, {
      reason: reason == null ? '' : String(reason),
    }),

  /** DELETE /api/customer/user-booking-blocks/{appUserId} */
  unblockUser: (appUserId) => instance.delete(`/customer/user-booking-blocks/${appUserId}`),

  /** POST /api/customer/user-reports — body: { appUserId, reportType, description } */
  reportUser: (appUserId, reportType, description = '') =>
    instance.post('/customer/user-reports', {
      appUserId: Number(appUserId),
      reportType: reportType == null ? '' : String(reportType),
      description: description == null ? '' : String(description),
    }),
};

// Slots API
export const slotsAPI = {
  // Generate slots (bulk)
  generateSlots: (slotData) => {
    return instance.post('/customer/generate-slots/generate', slotData);
  },

  // Add single slot
  addSlot: (slotData) => {
    return instance.post('/customer/generate-slots/add-slot', slotData);
  },

  // Get all slots with filters and pagination (admin/user slots)
  getSlots: (filters = {}, page = 0, limit = 10) => {
    const url = '/customer/generate-slots/user-slots';
    const params = new URLSearchParams();

    params.append('page', page + 1);
    params.append('limit', limit);

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    return instance.get(`${url}?${params.toString()}`);
  },

  // Get slot by ID
  getSlot: (slotId) => {
    return instance.get(`/customer/generate-slots/${slotId}`);
  },

  // Update slot
  updateSlot: (slotId, slotData) => {
    return instance.put(`/customer/generate-slots/${slotId}`, slotData);
  },

  // Delete slot
  deleteSlot: (slotId) => {
    return instance.delete(`/customer/generate-slots/${slotId}`);
  },

  // Bulk delete slots
  bulkDeleteSlots: (slotIds) => {
    return instance.post('/customer/generate-slots/bulk-delete', { slotIds });
  },

  // Get available slots for public booking
  getAvailableSlots: (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return instance.get(`/customer/generate-slots/available?${params}`);
  },

  // Confirm one or more slots as booked
  confirmSlots: (payload) => {
    return instance.post('/customer/generate-slots/confirm', payload);
  },

  // Clear slots for a given contact / status combination
  clearSlots: (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return instance.delete(`/customer/generate-slots/clear?${params}`);
  }
};

/** GET /api/customer/dashboard — KPIs, today counts, etc. */
export const dashboardAPI = {
  getSummary: () => instance.get('/customer/dashboard'),

  /** period: week | month | year — with startDate / endDate (ISO) */
  getAnalytics: (params = {}) =>
    instance.get('/customer/dashboard/analytics', { params }),

  /** Daily series for bookings vs slots chart */
  getSlotsByDay: (params = {}) =>
    instance.get('/customer/dashboard/slots-by-day', { params }),

  /** first-time vs return, completed/cancelled/total slots for range */
  getOverallInformation: (params = {}) =>
    instance.get('/customer/dashboard/overall-information', { params }),

  /** Today's bookings (optional status query) */
  getTodayBookings: (params = {}) =>
    instance.get('/customer/dashboard/today-bookings', { params }),

  /**
   * GET /api/customer/dashboard/user-views
   * Swagger parameters:
   * @param {Object} params
   * @param {string} [params.period='month'] - 'week' | 'month' | 'year'
   * @param {string} [params.startDate] - ISO date-time string
   * @param {string} [params.endDate] - ISO date-time string
   * @param {string} [params.module] - 'contact' | 'promotion'
   */
  getUserViews: (params = {}) => {
    const cleanParams = Object.entries(params).reduce((acc, [key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        acc[key] = val;
      }
      return acc;
    }, {});
    return instance.get('/customer/dashboard/user-views', { params: cleanParams });
  },
};

// Batch Jobs API
export const batchJobsAPI = {
  getList: () => instance.get('/customer/batch-jobs'),
  getById: (id) => instance.get(`/customer/batch-jobs/${id}`),
  run: (id) => instance.post(`/customer/batch-jobs/${id}/run`),
};

// Auto Slots Configuration API (create/update auto slot generation)
export const autoSlotsAPI = {
  configure: (payload) => instance.post('/customer/auto-slots/configure', payload),
};

// Customer Inquiries API
export const inquiriesAPI = {
  /**
   * GET /api/customer/inquiries?contactId=1&page=1&pageSize=20&status=Pending
   * contactId and status are optional.
   */
  getInquiries: (contactId = '', page = 1, pageSize = 20, status = '') => {
    const params = new URLSearchParams();
    if (contactId !== '' && contactId !== null && contactId !== undefined && contactId !== 'ALL') {
      params.append('contactId', String(contactId));
    }
    if (status && status !== 'ALL') {
      params.append('status', String(status));
      params.append('progressStatus', String(status));
    }
    params.append('page', String(Math.max(1, Number(page) || 1)));
    params.append('pageSize', String(Math.max(1, Number(pageSize) || 20)));
    return instance.get(`/customer/inquiries?${params.toString()}`);
  },

  /**
   * PUT /api/customer/inquiries/{inquiryId}/status
   * Fallback: PATCH /api/customer/inquiries/{inquiryId}/status
   * Status values: 'Pending', 'InProgress', 'Completed', 'Cancelled'
   */
  updateInquiryStatus: async (inquiryId, status) => {
    const payload = {
      status,
      progressStatus: status,
    };
    try {
      return await instance.put(`/customer/inquiries/${inquiryId}/status`, payload, {
        params: { status, progressStatus: status },
      });
    } catch (err) {
      const respStatus = err?.response?.status;
      if (respStatus === 404 || respStatus === 405) {
        return await instance.patch(`/customer/inquiries/${inquiryId}/status`, payload, {
          params: { status, progressStatus: status },
        });
      }
      throw err;
    }
  },

  /** Alias for updateInquiryStatus */
  updateStatus: (inquiryId, status) => inquiriesAPI.updateInquiryStatus(inquiryId, status),

  /** DELETE /api/customer/inquiries/{inquiryId} */
  deleteInquiry: (inquiryId) => instance.delete(`/customer/inquiries/${inquiryId}`),
};

// Customer notifications — GET list, POST mark one read, POST mark all read
export const notificationsAPI = {
  /** GET /api/customer/notifications?page&pageSize */
  getList: (page = 1, pageSize = 20, language = '') => {
    const params = {
      page: Math.max(1, Number(page) || 1),
      pageSize: Math.max(1, Number(pageSize) || 20),
    };
    if (language && language !== 'en') {
      params.language = language;
    }
    return instance.get('/customer/notifications', { params });
  },
  /** POST /api/customer/notifications/{id}/read */
  markRead: (id) => instance.post(`/customer/notifications/${id}/read`),
  /** POST /api/customer/notifications/read-all */
  markAllRead: () => instance.post('/customer/notifications/read-all'),
};

// Customer Verification API
export const verificationAPI = {
  // GET /api/customer/verification/status
  getStatus: (params = {}) => instance.get('/customer/verification/status', { params }),

  // POST /api/customer/verification/documents/submit
  submitDocuments: (formData) => {
    return instance.post('/customer/verification/documents/submit', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // PUT /api/customer/verification/documents/update — preview/edit flow (same multipart body as submit)
  updateDocuments: (formData) => {
    return instance.put('/customer/verification/documents/update', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // POST /api/customer/verification/confirm-submission
  confirmSubmission: (payload) =>
    instance.post('/customer/verification/confirm-submission', payload),

  // GET /api/customer/Authentication/profile
  getProfile: () => instance.get('/customer/Authentication/profile'),

  /** PUT — update profile (display name, etc.); body shape may vary by backend */
  updateProfile: (payload) => instance.put('/customer/Authentication/profile', payload),

  /** PUT /api/customer/Profile/display-name — company: businessName; freelancer: firstName + lastName */
  updateDisplayName: (payload) => instance.put('/customer/Profile/display-name', payload),

  /** PUT /api/customer/Profile/change-password — { oldPassword, newPassword, confirmPassword } */
  changePassword: (payload) => instance.post('/customer/Profile/change-password', payload),
};

// Support / Help Center API
export const supportAPI = {
  /** POST /api/customer/supports — { name, emailId, message, cityId } */
  submitSupport: (payload) => instance.post('/customer/supports', payload),
};

// Guests API
export const guestsAPI = {
  getGuests: (search = '', page = 1, pageSize = 50, includeArchived = false) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', String(page));
    params.append('pageSize', String(pageSize));
    params.append('includeArchived', String(includeArchived));
    return instance.get(`/customer/guests?${params.toString()}`);
  }
};

// Contact Chats API
export const contactChatsAPI = {
  // GET /api/customer/contact-chats/conversations?contactId={contactId}&search={search}
  getConversations: (contactId, search = '') => {
    const params = new URLSearchParams();
    if (contactId) params.append('contactId', contactId);
    if (search) params.append('search', search);
    const qs = params.toString();
    return instance.get(`/customer/contact-chats/conversations${qs ? `?${qs}` : ''}`);
  },

  // GET /api/customer/contact-chats/conversations/{conversationId}/messages
  getMessages: (conversationId, beforeMessageId = null, afterMessageId = null, limit = null) => {
    const params = new URLSearchParams();
    if (beforeMessageId) params.append('beforeMessageId', beforeMessageId);
    if (afterMessageId) params.append('afterMessageId', afterMessageId);
    if (limit) params.append('limit', limit);
    const qs = params.toString();
    return instance.get(`/customer/contact-chats/conversations/${conversationId}/messages${qs ? `?${qs}` : ''}`);
  },

  // POST /api/customer/contact-chats/conversations/{conversationId}/messages
  sendMessage: (conversationId, message) => {
    return instance.post(`/customer/contact-chats/conversations/${conversationId}/messages`, { message });
  },

  // POST /api/customer/contact-chats/conversations/{conversationId}/read
  markAsRead: (conversationId, lastReadMessageId = null) => {
    return instance.post(`/customer/contact-chats/conversations/${conversationId}/read`, { lastReadMessageId });
  }
};

// Helper to build multipart/form-data for Promotion Add/Update
export function buildPromotionFormData(data) {
  if (typeof FormData === 'undefined') return data;
  if (data instanceof FormData) return data;

  const fd = new FormData();
  const title = data.title || data.Title;
  if (title) fd.append('Title', String(title));

  const description = data.description || data.Description;
  if (description) fd.append('Description', String(description));

  // PromotionTypeId must be integer int32 (e.g. 1)
  let rawTypeId = data.promotionTypeId || data.PromotionTypeId || data.typeId || data.type;
  let parsedTypeId = parseInt(rawTypeId, 10);
  if (isNaN(parsedTypeId)) parsedTypeId = 1;
  fd.append('PromotionTypeId', String(parsedTypeId));

  // CityId must be integer int32 (e.g. 1)
  let rawCityId = data.cityId || data.CityId;
  let parsedCityId = parseInt(rawCityId, 10);
  if (isNaN(parsedCityId)) parsedCityId = 1;
  fd.append('CityId', String(parsedCityId));

  // PromotionDate format (DD/MM/YYYY or YYYY-MM-DD)
  let pDate = data.promotionDate || data.PromotionDate || '';
  if (pDate && pDate.includes('-')) {
    const parts = pDate.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      pDate = `${parts[2]}/${parts[1]}/${parts[0]}`; // Format as DD/MM/YYYY (15/08/2026)
    }
  }
  if (pDate) fd.append('PromotionDate', String(pDate));

  const pTime = data.promotionTime || data.PromotionTime;
  if (pTime) fd.append('PromotionTime', String(pTime));

  const cta = data.callToAction || data.CallToAction;
  if (cta) fd.append('callToAction', String(cta));

  const webUrl = data.webUrl || data.WebUrl;
  if (webUrl) fd.append('WebUrl', String(webUrl));

  const sharingUrl = data.sharingUrl || data.SharingUrl;
  if (sharingUrl) fd.append('SharingUrl', String(sharingUrl));

  const discountText = data.discountText || data.DiscountText;
  if (discountText) fd.append('DiscountText', String(discountText));

  const discountCoupon = data.discountCoupon || data.DiscountCoupon;
  if (discountCoupon) fd.append('DiscountCoupon', String(discountCoupon));

  const couponTitle = data.couponTitle || data.CouponTitle;
  if (couponTitle) fd.append('CouponTitle', String(couponTitle));

  const audience = data.targetedAudience || data.targetAudience || data.TargetAudience;
  if (audience) fd.append('targetAudience', String(audience));

  const details = data.details || data.promotionDetails || data.PromotionDetails;
  if (details) fd.append('promotionDetails', String(details));

  const ownerName = data.ownerName || data.OwnerName;
  if (ownerName) fd.append('OwnerName', String(ownerName));

  const terms = data.termsAndConditions || data.TermsAndConditions;
  if (terms) fd.append('TermsAndConditions', String(terms));

  // Append multiple whatYouGet items
  if (Array.isArray(data.whatYouGet)) {
    data.whatYouGet.forEach((item) => {
      if (item) fd.append('whatYouGet', String(item));
    });
  }

  // Append multiple picture image files
  if (Array.isArray(data.pictures)) {
    data.pictures.forEach((pic) => {
      if (typeof File !== 'undefined' && pic instanceof File) {
        fd.append('pictures', pic);
      }
    });
  } else if (typeof FileList !== 'undefined' && data.pictures instanceof FileList) {
    Array.from(data.pictures).forEach((pic) => {
      fd.append('pictures', pic);
    });
  } else if (typeof File !== 'undefined' && data.pictureFile instanceof File) {
    fd.append('pictures', data.pictureFile);
  }

  // Append picturesKeep (kept existing picture URLs)
  if (Array.isArray(data.picturesKeep)) {
    data.picturesKeep.forEach((pic) => {
      if (pic) fd.append('picturesKeep', String(pic));
    });
  } else if (data.picturesKeep) {
    fd.append('picturesKeep', String(data.picturesKeep));
  }

  // Append picturesRemove (removed existing picture URLs)
  if (Array.isArray(data.picturesRemove)) {
    data.picturesRemove.forEach((pic) => {
      if (pic) fd.append('picturesRemove', String(pic));
    });
  } else if (data.picturesRemove) {
    fd.append('picturesRemove', String(data.picturesRemove));
  }

  return fd;
}

// Promotions API
export const promotionsAPI = {
  getPromotions: (params = {}) => {
    const query = new URLSearchParams();

    const cityIdVal = parseInt(params.cityId, 10);
    query.append('cityId', isNaN(cityIdVal) ? 0 : cityIdVal);

    const pageVal = parseInt(params.page, 10);
    query.append('page', isNaN(pageVal) ? 1 : pageVal);

    const pageSizeVal = parseInt(params.pageSize, 10);
    query.append('pageSize', isNaN(pageSizeVal) ? 50 : pageSizeVal);

    if (params.search) query.append('search', params.search);
    if (params.type && params.type !== 'all') query.append('type', params.type);
    if (params.isActive !== undefined && params.isActive !== null && params.isActive !== 'all') {
      query.append('isActive', params.isActive);
    }
    const qs = query.toString();
    return instance.get(`/customer/promotions${qs ? `?${qs}` : ''}`);
  },

  getPromotion: (promotionId) => instance.get(`/customer/promotions/${promotionId}`),

  getPromotionTypes: (language = 'en') =>
    instance.get(`/customer/promotions/types?language=${encodeURIComponent(language)}`),

  createPromotion: (data) => {
    const formData = buildPromotionFormData(data);
    return instance.post('/customer/promotions', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  updatePromotion: (promotionId, data) => {
    const formData = buildPromotionFormData(data);
    return instance.put(`/customer/promotions/${promotionId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  deletePromotion: (promotionId) => instance.delete(`/customer/promotions/${promotionId}`),

  togglePromotionStatus: (promotionId, isActive) =>
    instance.put('/customer/promotions/status', {
      promotionId: parseInt(promotionId, 10) || Number(promotionId) || 0,
      isActive: Boolean(isActive)
    }),
};


export const customPushNotificationsAPI = {
  // GET list of all custom push notifications
  getNotifications: () => {
    return instance.get('/customer/custom-push-notifications');
  },

  // GET custom push notification by ID
  getNotificationById: (id) => {
    return instance.get(`/customer/custom-push-notifications/${id}`);
  },

  // POST create custom push notification — body: { title, message, targetType, targetEntityId }
  createNotification: (data) => {
    const targetType = data.targetType || data.category || 'Custom';
    const targetEntityId = data.targetEntityId !== undefined
      ? data.targetEntityId
      : (data.contactId || data.promotionId || null);

    return instance.post('/customer/custom-push-notifications', {
      title: data.title,
      message: data.message || data.body,
      targetType,
      targetEntityId,
      category: targetType,
      ...(data.contactId ? { contactId: data.contactId } : {}),
      ...(data.promotionId ? { promotionId: data.promotionId } : {}),
    });
  },

  // PUT update custom push notification — body: { title, message, targetType, targetEntityId }
  updateNotification: (id, data) => {
    const targetType = data.targetType || data.category || 'Custom';
    const targetEntityId = data.targetEntityId !== undefined
      ? data.targetEntityId
      : (data.contactId || data.promotionId || null);

    return instance.put(`/customer/custom-push-notifications/${id}`, {
      title: data.title,
      message: data.message || data.body,
      targetType,
      targetEntityId,
      category: targetType,
      ...(data.contactId ? { contactId: data.contactId } : {}),
      ...(data.promotionId ? { promotionId: data.promotionId } : {}),
    });
  },

  // DELETE custom push notification by ID
  deleteNotification: (id) => {
    return instance.delete(`/customer/custom-push-notifications/${id}`);
  },
};

export const categoryPushNotificationsAPI = {
  // GET history of category push notifications: GET /customer/category-push-notifications/history?page=1&pageSize=20
  getHistory: (page = 1, pageSize = 20) => {
    return instance.get('/customer/category-push-notifications/history', {
      params: { page, pageSize },
    });
  },

  // POST send category push notification: POST /customer/category-push-notifications/send
  // body: { module, entityId, title, body, lookbackDays }
  sendNotification: (payload) => {
    return instance.post('/customer/category-push-notifications/send', {
      module: payload.module || 'Contact',
      entityId: payload.entityId !== undefined ? Number(payload.entityId) : null,
      title: payload.title || '',
      body: payload.body || payload.message || '',
      lookbackDays: payload.lookbackDays !== undefined ? Number(payload.lookbackDays) : 30,
    });
  },
};

